import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  Divider,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTheme } from "../../ThemeContext";
import { Button, TextBox, Dropdown } from "@voicecare.ai/ui-library";
import { Button as VCButton } from "@voicecare.ai/ui-library";

// Helper function to ensure valid grid columns
const getGridColumns = (fieldsPerRow) => {
  const allowed = [1, 2, 3, 4, 6, 12];
  return allowed.includes(fieldsPerRow) ? 12 / fieldsPerRow : 12;
};

// Helper function to get nested field errors with improved handling
const getNestedFieldError = (errors, fieldName) => {
  if (!fieldName || !errors) return null;
  
  // Handle simple field names first
  if (errors[fieldName]) {
    return errors[fieldName];
  }
  
  // Handle nested field names like 'affiliations.0.facilityId'
  if (fieldName.includes('.')) {
    const fieldParts = fieldName.split('.');
    let current = errors;
    
    for (let i = 0; i < fieldParts.length; i++) {
      const part = fieldParts[i];
      
      if (current && current[part] !== undefined) {
        current = current[part];
      } else {
        return null;
      }
    }
    
    return current;
  }
  
  return null;
};

// Alternative error getter using React Hook Form's built-in method
const getFieldError = (errors, fieldName) => {
  // First try our nested error helper
  const nestedError = getNestedFieldError(errors, fieldName);
  if (nestedError) return nestedError;
  
  // Fallback: try to find error by exact key match
  const errorKeys = Object.keys(errors);
  const matchingKey = errorKeys.find(key => key === fieldName);
  if (matchingKey) {
    return errors[matchingKey];
  }
  
  return null;
};

const OForm = ({
  formTitle,
  initialValues,
  fields: getFields,
  validationSchema,
  onSubmit,
  fieldsPerRow = 2,
  handleBack = null,
  submitButtonText = "Submit",
  handleResetButton = null,
  headerButtonLabel,
  headerButtonOnClick,
  submitting = false,
  scrollableSections = [],
  additionalButtons = [],
  additionalHeaderButtons = [],
}) => {
  const { themeMode, currentTheme } = useTheme();

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    setValue,
    watch,
    getValues,
    reset,
    control,
    formState: { isSubmitting, errors },
  } = methods;

  // Track last initialValues to avoid unnecessary form resets
  const lastInitialValuesRef = useRef(initialValues);

  const fields =
    typeof getFields === "function"
      ? getFields(watch, { setValue, getValues })
      : getFields;

  const fieldsBySection = fields.reduce((acc, field) => {
    const { section = "default" } = field;
    if (!acc[section]) acc[section] = [];
    acc[section].push(field);
    return acc;
  }, {});

  useEffect(() => {
    const prev = lastInitialValuesRef.current;

    // Only reset the form when initialValues have meaningfully changed
    const prevString = JSON.stringify(prev);
    const nextString = JSON.stringify(initialValues);

    if (prevString !== nextString) {
      reset(initialValues);
      lastInitialValuesRef.current = initialValues;
    }
  }, [initialValues, reset]);

  const renderField = (field) => {
    const {
      fieldName,
      renderType,
      label,
      extraProps = {},
      disabled,
      options = [],
      placeholder,
      iconUrl,
      type,
      hidden,
      isCreatable,
    } = field;

    // Check if field should be hidden (support both boolean and function)
    const shouldHide = typeof hidden === "function" ? hidden(watch()) : hidden;
    if (shouldHide) {
      return null;
    }

    // Get the field error using the helper function
    const fieldError = getFieldError(errors, fieldName);

    // Debug: log errors for specific fields we're interested in
    if (fieldName === 'specialties' || fieldName.includes('affiliations')) {
      console.log(`Field "${fieldName}" error:`, fieldError);
      console.log('All errors:', errors);
      console.log(`Field "${fieldName}" configuration:`, field);
    }

    let textBoxProps = {
      textBoxText: currentTheme.loginPage.textBoxText,
      themeMode,
      placeholder: placeholder || label,
      iconUrl,
      ...extraProps,
    };

    if (themeMode === "dark") {
      textBoxProps.textBoxBackground = currentTheme.loginPage.textBoxBackground;
    }

    switch (renderType) {
      case "checkbox":
        return (
          <FormControlLabel
            control={
              <Controller
                name={fieldName}
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={!!field.value}
                    disabled={disabled}
                  />
                )}
              />
            }
            label={label}
          />
        );

      case "select":
        return (
          <Controller
            name={fieldName}
            control={control}
            render={({ field, fieldState }) => {
              // Debug field state
              if (fieldName === 'specialties' || fieldName.includes('affiliations')) {
                console.log(`Field "${fieldName}" state:`, {
                  field,
                  fieldState,
                  error: fieldState.error,
                  hasError: !!fieldState.error,
                  fieldError,
                  finalError: !!fieldError || !!fieldState.error
                });
              }
              
              return (
                <Dropdown
                  label={label}
                  options={options}
                  creatable={isCreatable}
                  placeholder={placeholder || label}                  
                  value={
                    type === "multi-select"
                      ? Array.isArray(field.value)
                        ? field.value.map(
                            (val) =>
                              options.find((opt) => opt.value === val) || {
                                label: val,
                                value: val,
                              }
                          )
                        : []
                      : options.find((opt) => opt.value === field.value) ||
                        (field.value
                          ? { label: field.value, value: field.value }
                          : null)
                  }
                  onChange={(selected, actionMeta) => {
                    if (type === "multi-select") {
                      field.onChange(
                        Array.isArray(selected)
                          ? selected.map((opt) => opt.value)
                          : []
                      );
                    } else {
                      field.onChange(selected ? selected.value : "");
                    }
                  }}
                  themeMode={themeMode}
                  type={type}
                  isSearchable={true}
                  disabled={disabled}
                  width="92%"
                  error={!!fieldError || !!fieldState.error}
                  helperText={fieldError?.message || fieldState.error?.message}
                  {...extraProps}
                />
              );
            }}
          />
        );

      case "date":
        return (
          <Controller
            name={fieldName}
            control={control}
            render={({ field }) => (
              <TextBox
                {...textBoxProps}
                type="text"
                value={field.value || ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={disabled}
                placeholder={placeholder || label}
                style={{ width: "90%", ...(textBoxProps.style || {}) }}
                error={!!fieldError}
                helperText={fieldError?.message}
              />
            )}
          />
        );

      case "multiline":
        return (
          <Controller
            name={fieldName}
            control={control}
            render={({ field }) => (
              <TextBox
                {...textBoxProps}
                type="text"
                value={field.value || ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={disabled}
                placeholder={placeholder || label}
                style={{
                  width: "90%",
                  minHeight: 80,
                  ...(textBoxProps.style || {}),
                }}
                error={!!fieldError}
                helperText={fieldError?.message}
              />
            )}
          />
        );

      case "custom":
        // Check if customComponent is provided (new pattern)
        if (field.customComponent) {
          const CustomComponent = field.customComponent;
          return (
            <CustomComponent
              fieldName={fieldName}
              control={control}
              errors={errors}
              fieldError={fieldError}
              hasError={!!fieldError}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              disabled={disabled}
              {...extraProps}
            />
          );
        }
        
        // Fallback to legacy render function (backward compatibility)
        if (typeof field.render === "function") {
          return field.render({ 
            errors, 
            fieldError, 
            hasError: !!fieldError,
            control,
            watch,
            setValue,
            getValues
          });
        }
        return null;

      default:
        return (
          <Controller
            name={fieldName}
            control={control}
            render={({ field }) => (
              <TextBox
                {...textBoxProps}
                type={renderType}
                value={field.value || ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={disabled}
                label={label}
                placeholder={placeholder || label}
                style={{
                  width: "90%",
                  borderRadius: "6px",
                  height: "1.6rem",
                  ...(textBoxProps.style || {}),
                }}
                error={!!fieldError}
                helperText={fieldError?.message}
              />
            )}
          />
        );
    }
  };

  // Determine if there are sections
  const sectionNames = Object.keys(fieldsBySection).filter(
    (s) => s !== "default"
  );
  const hasSections = sectionNames.length > 0;

  const handleReset = () => {
    reset(initialValues);
    return;
  };

  return (
    <FormProvider {...methods}>
      <form 
        onSubmit={handleSubmit(onSubmit)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        <Grid
          container
          sx={{
            p: 2,
            mb: 1,
            backgroundColor: currentTheme.callhistory.mainBackground,
            borderRadius: "8px",
          }}
        >
          {formTitle && (
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h5"
                sx={{ color: currentTheme.text.primary }}
              >
                {formTitle}
              </Typography>
              {!hasSections && headerButtonLabel && headerButtonOnClick && (
                <VCButton
                  variant="contained"
                  onClick={headerButtonOnClick}
                  label={headerButtonLabel}
                  backgroundColor={currentTheme.loginPage.textBoxBackground}
                  color={currentTheme.text.primary}
                  style={{ borderRadius: "6px" }}
                />
              )}
            </Grid>
          )}
        </Grid>

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            // Custom scrollbar styles
            scrollbarWidth: 'thin',
            scrollbarColor: `${themeMode === 'dark' ? '#666' : '#888'} transparent`,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: themeMode === 'dark' ? '#363354' : '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: themeMode === 'dark' ? '#666' : '#888',
              borderRadius: '4px',
              '&:hover': {
                background: themeMode === 'dark' ? '#888' : '#666',
              },
            },
          }}
        >
          {Object.entries(fieldsBySection).map(
            ([section, sectionFields], sectionIndex) => (
              <Box
                key={sectionIndex}
                sx={{
                  mb: 4,
                  p: 2,
                  backgroundColor: currentTheme.callhistory.mainBackground,
                  borderRadius: "8px",
                  maxHeight: scrollableSections.includes(section)
                    ? "35vh"
                    : "auto",
                  overflow: scrollableSections.includes(section)
                    ? "auto"
                    : "visible",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: themeMode === "dark" ? "#363354" : "#f1f1f1",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: themeMode === "dark" ? "#666" : "#888",
                    borderRadius: "4px",
                    "&:hover": {
                      background: themeMode === "dark" ? "#888" : "#666",
                    },
                  },
                }}
              >
              {section !== "default" && (
                <Grid container alignItems="center" sx={{ mb: 1 }}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      position: "sticky",
                      top: 0,
                      backgroundColor: currentTheme.callhistory.mainBackground,
                      zIndex: 1,
                      pb: 1,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                      {sectionIndex === 0 && headerButtonOnClick && (
                        <IconButton
                          onClick={headerButtonOnClick}
                          sx={{
                            color: currentTheme.text.primary,
                            padding: '4px',
                            '&:hover': {
                              backgroundColor: currentTheme.border.primary,
                            },
                          }}
                          aria-label={headerButtonLabel || "Back"}
                        >
                          <ArrowBackIcon />
                        </IconButton>
                      )}
                      <Typography
                        variant="h6"
                        sx={{ color: currentTheme.text.primary, mb: 0 }}
                      >
                        {section}
                      </Typography>
                      {sectionFields.find(field => field.sectionInfo) && (
                        <Tooltip
                          title={
                            <Typography variant="body2" sx={{ p: 0.5 }}>
                              {sectionFields.find(field => field.sectionInfo)?.sectionInfo}
                            </Typography>
                          }
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: `${themeMode === 'dark' ? '#3f3c57' : '#e2e2e2'} !important`,
                                color: `${themeMode === 'dark' ? '#ffffff' : '#000000'} !important`,
                                fontSize: '14px',
                                '& .MuiTooltip-arrow': {
                                  color: `${themeMode === 'dark' ? '#3f3c57' : '#e2e2e2'} !important`,
                                },
                              },
                            },
                          }}
                        >
                          <InfoOutlinedIcon
                            sx={{ fontSize: 18, ml: 0.5, color: currentTheme.text.primary }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                    {sectionIndex === 0 && additionalHeaderButtons.length > 0 && (
                      <Box display="flex" alignItems="center" gap={1.5}>
                        {additionalHeaderButtons.map((button, index) => (
                          <Box key={index}>
                            {button}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Divider
                      sx={{ mb: 2, borderColor: currentTheme.border.primary }}
                    />
                  </Grid>
                </Grid>
              )}
              <Grid container spacing={2}>
                {sectionFields.map((field) => (
                  <Grid
                    item
                    xs={12}
                    md={field.gridColumns || getGridColumns(fieldsPerRow)}
                    key={field.fieldName}
                  >
                    {renderField(field)}
                  </Grid>
                ))}
              </Grid>
            </Box>
          )
        )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mt: 2,
            p: 2,
            backgroundColor: currentTheme.callhistory.mainBackground,
            borderRadius: "8px",
            flexShrink: 0, // Prevent buttons from shrinking
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Box sx={{ display: "flex", gap: 2 }}>
            {additionalButtons.map((button, index) => (
              <React.Fragment key={index}>{button}</React.Fragment>
            ))}
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleReset}
              label="Reset"
              backgroundColor={currentTheme.loginPage.textBoxBackground}
              color={currentTheme.text.primary}
              style={{ borderRadius: "6px" }}
            />
            <Button
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting || submitting}
              style={{ borderRadius: "6px" }}
              label={submitButtonText}
            />
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};

OForm.propTypes = {
  formTitle: PropTypes.string,
  initialValues: PropTypes.object.isRequired,
  fields: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(
      PropTypes.shape({
        fieldName: PropTypes.string.isRequired,
        isRequired: PropTypes.bool,
        renderType: PropTypes.oneOf([
          "text",
          'phone',
          "password",
          "email",
          "number",
          "date",
          "select",
          "checkbox",
          "multiline",
          "custom",
        ]).isRequired,
        label: PropTypes.string.isRequired,
        section: PropTypes.string,
        sectionInfo: PropTypes.string,
        extraProps: PropTypes.object,
        customComponent: PropTypes.elementType, // Optional custom component
        render: PropTypes.func, // Legacy render function
        gridColumns: PropTypes.number, // Optional custom grid columns (overrides fieldsPerRow)
        options: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
              .isRequired,
          })
        ),
      })
    )
  ]).isRequired,
  validationSchema: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  fieldsPerRow: PropTypes.number,
  handleBack: PropTypes.func,
  submitButtonText: PropTypes.string,
  handleResetButton: PropTypes.func,
  headerButtonLabel: PropTypes.string,
  headerButtonOnClick: PropTypes.func,
  submitting: PropTypes.bool,
  scrollableSections: PropTypes.arrayOf(PropTypes.string),
  additionalButtons: PropTypes.arrayOf(PropTypes.element),
  additionalHeaderButtons: PropTypes.arrayOf(PropTypes.element),
};

OForm.defaultProps = {
  scrollableSections: ["Affiliations"],
};

export default OForm;
